<template>
  <InstructorLayout>
    <div class="instructor-mypage">
      <h1>👤 マイページ</h1>

      <div v-if="loadError" class="error-banner">
        {{ loadError }}
      </div>

      <template v-else>
        <!-- 講師本名（給与・従業員管理用） -->
        <section class="card">
          <h2>講師本名</h2>
          <p class="section-desc">給与・従業員管理に使う正式な名前です。</p>
          <form @submit.prevent="saveRealName" class="form">
            <label class="form-label">
              <span>講師本名</span>
              <input
                v-model="form.realName"
                type="text"
                class="form-input"
                placeholder="例：山田 太郎"
                maxlength="100"
              />
            </label>
            <button type="submit" class="btn btn-primary" :disabled="isSavingRealName">
              {{ isSavingRealName ? '保存中...' : '保存' }}
            </button>
          </form>
          <p v-if="realNameMessage" class="form-message" :class="realNameMessageType">{{ realNameMessage }}</p>
        </section>

        <!-- 講師名（オトエイゴ内で生徒に表示する名前） -->
        <section class="card">
          <h2>講師名</h2>
          <p class="section-desc">オトエイゴ内で生徒に表示する名前です。英語の先生用の偽名（例：ジョージ）を設定できます。</p>
          <form @submit.prevent="saveDisplayName" class="form">
            <label class="form-label">
              <span>講師名</span>
              <input
                v-model="form.displayName"
                type="text"
                class="form-input"
                placeholder="例：ジョージ"
                maxlength="100"
              />
            </label>
            <button type="submit" class="btn btn-primary" :disabled="isSavingDisplayName">
              {{ isSavingDisplayName ? '保存中...' : '保存' }}
            </button>
          </form>
          <p v-if="displayNameMessage" class="form-message" :class="displayNameMessageType">{{ displayNameMessage }}</p>
        </section>

        <!-- メールアドレス変更 -->
        <section class="card">
          <h2>メールアドレス</h2>
          <p class="section-desc">ログインに使用するメールアドレスを変更します。変更後、確認メールが送信される場合があります。</p>
          <form @submit.prevent="saveEmail" class="form">
            <label class="form-label">
              <span>新しいメールアドレス</span>
              <input
                v-model="form.email"
                type="email"
                class="form-input"
                placeholder="メールアドレスを入力"
                autocomplete="email"
              />
            </label>
            <button type="submit" class="btn btn-primary" :disabled="isSavingEmail">
              {{ isSavingEmail ? '更新中...' : 'メールアドレスを更新' }}
            </button>
          </form>
          <p v-if="emailMessage" class="form-message" :class="emailMessageType">{{ emailMessage }}</p>
        </section>

        <!-- パスワード変更（リセットメール） -->
        <section class="card">
          <h2>パスワード変更</h2>
          <p class="section-desc">
            パスワードを変更する場合は、登録中のメールアドレス（<strong>{{ currentEmail }}</strong>）にリセット用リンクを送信します。メールのリンクから新しいパスワードを設定してください。
          </p>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isSendingReset || !currentEmail"
            @click="sendPasswordResetEmail"
          >
            {{ isSendingReset ? '送信中...' : 'パスワード変更用メールを送信' }}
          </button>
          <p v-if="passwordMessage" class="form-message" :class="passwordMessageType">{{ passwordMessage }}</p>
        </section>

        <!-- ログアウト -->
        <section class="card logout-section">
          <button type="button" class="btn btn-danger logout-btn" @click="handleLogout">
            🚪 ログアウト
          </button>
        </section>
      </template>

      <!-- グローバルナビゲーション -->
      <InstructorNavigation />
    </div>
  </InstructorLayout>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../../supabaseClient'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'
import InstructorNavigation from '../../components/layout/InstructorNavigation.vue'

const router = useRouter()

const loadError = ref('')
const currentEmail = ref('')
const currentUserId = ref(null)

const form = reactive({
  realName: '',
  displayName: '',
  email: ''
})

const isSavingRealName = ref(false)
const isSavingDisplayName = ref(false)
const isSavingEmail = ref(false)
const isSendingReset = ref(false)
const realNameMessage = ref('')
const realNameMessageType = ref('success')
const displayNameMessage = ref('')
const displayNameMessageType = ref('success')
const emailMessage = ref('')
const emailMessageType = ref('success')
const passwordMessage = ref('')
const passwordMessageType = ref('success')

const clearRealNameMessage = () => {
  realNameMessage.value = ''
}
const clearDisplayNameMessage = () => {
  displayNameMessage.value = ''
}
const clearEmailMessage = () => {
  emailMessage.value = ''
}
const clearPasswordMessage = () => {
  passwordMessage.value = ''
}

async function loadProfile() {
  loadError.value = ''
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      loadError.value = 'ログインが必要です'
      return
    }
    currentUserId.value = user.id
    currentEmail.value = user.email ?? ''
    form.email = currentEmail.value

    const { data: profile, error } = await supabase
      .from('users_phase2')
      .select('full_name, instructor_display_name, email')
      .eq('id', user.id)
      .single()

    if (error) {
      console.warn('users_phase2 取得:', error)
      form.realName = user.user_metadata?.full_name ?? ''
      return
    }
    form.realName = profile?.full_name ?? ''
    form.displayName = profile?.instructor_display_name ?? ''
    if (profile?.email) form.email = profile.email
  } catch (e) {
    console.error('プロフィール読み込みエラー:', e)
    loadError.value = 'プロフィールの読み込みに失敗しました'
  }
}

async function saveRealName() {
  if (!currentUserId.value) return
  isSavingRealName.value = true
  realNameMessage.value = ''
  try {
    const { error } = await supabase
      .from('users_phase2')
      .update({ full_name: form.realName.trim() || null, updated_at: new Date().toISOString() })
      .eq('id', currentUserId.value)

    if (error) throw error
    realNameMessageType.value = 'success'
    realNameMessage.value = '講師本名を保存しました'
    setTimeout(clearRealNameMessage, 3000)
  } catch (e) {
    console.error('講師本名保存エラー:', e)
    realNameMessageType.value = 'error'
    realNameMessage.value = e.message || '保存に失敗しました'
  } finally {
    isSavingRealName.value = false
  }
}

async function saveDisplayName() {
  if (!currentUserId.value) return
  isSavingDisplayName.value = true
  displayNameMessage.value = ''
  try {
    const { error } = await supabase
      .from('users_phase2')
      .update({ instructor_display_name: form.displayName.trim() || null, updated_at: new Date().toISOString() })
      .eq('id', currentUserId.value)

    if (error) throw error
    displayNameMessageType.value = 'success'
    displayNameMessage.value = '講師名を保存しました'
    setTimeout(clearDisplayNameMessage, 3000)
  } catch (e) {
    console.error('講師名保存エラー:', e)
    displayNameMessageType.value = 'error'
    displayNameMessage.value = e.message || '保存に失敗しました'
  } finally {
    isSavingDisplayName.value = false
  }
}

async function saveEmail() {
  if (!currentUserId.value) return
  const newEmail = form.email?.trim()
  if (!newEmail) {
    emailMessageType.value = 'error'
    emailMessage.value = 'メールアドレスを入力してください'
    return
  }
  if (newEmail === currentEmail.value) {
    emailMessageType.value = 'success'
    emailMessage.value = '変更ありません'
    setTimeout(clearEmailMessage, 2000)
    return
  }
  isSavingEmail.value = true
  emailMessage.value = ''
  try {
    const { error } = await supabase.auth.updateUser({ email: newEmail })
    if (error) throw error

    const { error: profileError } = await supabase
      .from('users_phase2')
      .update({ email: newEmail, updated_at: new Date().toISOString() })
      .eq('id', currentUserId.value)
    if (profileError) console.warn('users_phase2 email 同期:', profileError)

    currentEmail.value = newEmail
    emailMessageType.value = 'success'
    emailMessage.value = 'メールアドレスを更新しました。確認メールが送信されている場合はご確認ください。'
    setTimeout(clearEmailMessage, 5000)
  } catch (e) {
    console.error('メール更新エラー:', e)
    emailMessageType.value = 'error'
    emailMessage.value = e.message || 'メールアドレスの更新に失敗しました'
  } finally {
    isSavingEmail.value = false
  }
}

async function sendPasswordResetEmail() {
  const email = currentEmail.value
  if (!email) {
    passwordMessageType.value = 'error'
    passwordMessage.value = 'メールアドレスが取得できません'
    return
  }
  isSendingReset.value = true
  passwordMessage.value = ''
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) throw error
    passwordMessageType.value = 'success'
    passwordMessage.value = 'パスワード変更用のメールを送信しました。登録メールをご確認ください。'
    setTimeout(clearPasswordMessage, 6000)
  } catch (e) {
    console.error('パスワードリセット送信エラー:', e)
    passwordMessageType.value = 'error'
    passwordMessage.value = e.message || 'メールの送信に失敗しました'
  } finally {
    isSendingReset.value = false
  }
}

async function handleLogout() {
  try {
    await supabase.auth.signOut()
    router.push('/instructor/login')
  } catch (error) {
    console.error('ログアウトエラー:', error)
  }
}

onMounted(() => {
  loadProfile()
})
</script>

<style scoped>
.instructor-mypage {
  max-width: 560px;
  margin: 0 auto;
  padding: 1rem;
  padding-bottom: 5rem;
}

h1 {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.error-banner {
  background: #fef2f2;
  color: #b91c1c;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card h2 {
  font-size: 1.1rem;
  margin: 0 0 0.5rem 0;
  color: #374151;
}

.section-desc {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.form {
  margin-top: 0.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.75rem;
}

.form-label span {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}

.form-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.btn {
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

.logout-section {
  margin-bottom: 1.5rem;
}

.logout-btn {
  width: 100%;
  padding: 0.75rem;
}

.form-message {
  margin: 0.75rem 0 0 0;
  font-size: 0.875rem;
}

.form-message.success {
  color: #059669;
}

.form-message.error {
  color: #dc2626;
}
</style>
